import { useEffect, useState } from 'react';
import http from '../services/httpClient';

/**
 * Does this business have the banquet lane?
 *
 * WHY THIS ASKS THE SERVER INSTEAD OF READING A SETTING
 * -----------------------------------------------------
 * Enablement is decided by the business TYPE — "Restaurant + Banquet" carries
 * BusinessTypes.has_banquet — with a BusinessSettings row as an override that
 * is ABSENT in the ordinary case.
 *
 * So a client that read the setting directly would see `false` for every
 * genuine banquet business, hide the menu entry and the Reports section from
 * exactly the customers the feature is for, and do it while the API served
 * their requests perfectly well. That was this hook's first version, and the
 * bug it would have shipped is the reason it now asks one question of one
 * resolver: /auth/user returns `isBanquetEnabled`, computed by the same
 * utils/banquetFlag.js that guards the routes.
 *
 * Same shape as `isQRBilling`, which the app already reads from this endpoint
 * rather than deriving from billing_model_id.
 *
 * DEFAULTS FALSE, AND STAYS FALSE UNTIL PROVEN OTHERWISE. This decides whether
 * two menu entries and a Reports section exist; answering `true` optimistically
 * would flash them on every page load for every business on the platform. The
 * backend enforces the same answer on every route, so a stale `true` here still
 * cannot reach a banquet endpoint.
 *
 * WHY A MODULE-LEVEL MEMO
 * -----------------------
 * Three unrelated screens ask this — the TSR page, the Reports list and the
 * report page — and none shares an ancestor worth threading a provider
 * through. 60s is long enough to stop /auth/user being hit on every
 * navigation, short enough that a change shows up without a reload.
 */

const TTL_MS = 60 * 1000;

const readBusinessId = () => {
  const direct = Number(localStorage.getItem('businessId'));
  if (Number.isFinite(direct) && direct > 0) return direct;
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Number(user.businessId || user.business_id || user.businessid) || 0;
  } catch {
    return 0;
  }
};

/** businessId -> { value, expiresAt } */
const cache = new Map();
/** businessId -> in-flight promise, so three mounting screens make one request. */
const inflight = new Map();

async function fetchEnabled() {
  try {
    const res = await http.get('/auth/user');
    const data = (res && res.data) || res || {};
    return data.isBanquetEnabled === true || data.business?.isBanquetEnabled === true;
  } catch {
    // A failed lookup must leave the app exactly as it is today.
    return false;
  }
}

/** Clears the memo — for tests, and after anything that could change the answer. */
export function clearBanquetFlagCache() {
  cache.clear();
  inflight.clear();
}

export default function useBanquetFlag() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    const businessId = readBusinessId();
    if (!businessId) return undefined;

    const cached = cache.get(businessId);
    if (cached && cached.expiresAt > Date.now()) {
      setEnabled(cached.value);
      return undefined;
    }

    let promise = inflight.get(businessId);
    if (!promise) {
      promise = fetchEnabled()
        .then((value) => {
          cache.set(businessId, { value, expiresAt: Date.now() + TTL_MS });
          return value;
        })
        .finally(() => inflight.delete(businessId));
      inflight.set(businessId, promise);
    }

    promise.then((value) => { if (alive) setEnabled(value); });
    return () => { alive = false; };
  }, []);

  return enabled;
}
